import React, { Dispatch, SetStateAction, useCallback, useMemo, useRef } from 'react';
import classes from './MediaGrid.module.scss';
import { MediaItem, MediaItemSelectedType } from '../../../../models/media';
import { LightboxModal } from '../LightboxModal';
import { MediaGridItem } from '../MediaGridItem';
import Masonry, { MasonryClasses } from '@mui/lab/Masonry';
import { FloatingActionBox } from '../FloatingActionBox';
import { ActionButtonGroup } from '../ActionButtonGroup';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { ActionMenuList } from '../ActionMenuList';
import { ActionProperties } from '../ActionMenuList/ActionMenuList.view';
import classNames from 'classnames';
import { MediaType } from '../../../../models/graphql';
// import { useLockBodyScroll } from '../../../../hooks/useLockBodyScroll';
// import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';

export enum MediaGridContext {
  'PAGE' = 1,
  'MODAL',
  'FIND_IMAGES',
  'POST_EDITOR_MEDIA_LIBRARY',
  'POST_EDITOR_FIND_IMAGES'
}

export type PrimaryButtonOptional = {
  primaryButtonTitle?: string;
  primaryButtonAction?: never;
};

export type PrimaryButtonMandatory = {
  primaryButtonTitle: string;
  primaryButtonAction: (media: MediaItem[]) => void;
};

export type SecondaryButtonOptional = {
  secondaryButtonTitle?: string;
  secondaryButtonAction?: never;
};

export type SecondaryButtonMandatory = {
  secondaryButtonTitle: string;
  secondaryButtonAction: (media: MediaItem[]) => void;
};

type PrimaryButtonType = PrimaryButtonOptional | PrimaryButtonMandatory;
type SecondaryButtonType = SecondaryButtonOptional | SecondaryButtonMandatory;

export type MediaGridViewProps = PrimaryButtonType &
  SecondaryButtonType & {
    mediaItems: MediaItem[];
    context: MediaGridContext;
    showLightboxModal: boolean;
    setShowLightboxModal: (show: boolean) => void;
    selectedMediaItems: MediaItemSelectedType[];
    setSelectedMediaItems: React.Dispatch<React.SetStateAction<MediaItemSelectedType[]>>;
    selectedMediaItemForLightbox: MediaItem | undefined;
    setSelectedMediaItemForLightbox: (item: MediaItem | undefined) => void;
    showDeleteConfirmationModal: boolean;
    setShowDeleteConfirmationModal: (show: boolean) => void;
    checkboxHandler: (mediaItem: MediaItemSelectedType, e: React.MouseEvent<HTMLInputElement>) => void;
    parentWidth?: number;
    imagesIdsWithErrorSelected: MediaItemSelectedType[];
    setImagesIdsWithErrorSelected: Dispatch<SetStateAction<MediaItemSelectedType[]>>;
    isUnsplash?: boolean;
  };

export const MediaGridView = (props: MediaGridViewProps): JSX.Element => {
  const masonryRef = useRef<HTMLDivElement | null>(null);
  const {
    context,
    mediaItems,
    primaryButtonAction,
    secondaryButtonAction,
    setShowLightboxModal,
    selectedMediaItems,
    setSelectedMediaItemForLightbox,
    checkboxHandler,
    setImagesIdsWithErrorSelected,
    imagesIdsWithErrorSelected
  } = props;

  const getSelectedMediaItems = (selected: MediaItemSelectedType[] = []): MediaItem[] => {
    return props.mediaItems.filter(item =>
      selected.some(selectedItem => selectedItem.id === item.id && selectedItem.type === item.type)
    );
  };

  const actionMenuListItems = useCallback(
    (mediaItem: MediaItem): ContainerListItem[] => {
      let actionProps: ActionProperties | undefined;

      const lightBoxAction = () => {
        setSelectedMediaItemForLightbox(mediaItem);
        setShowLightboxModal(true);
      };

      switch (context) {
        case MediaGridContext.MODAL:
          actionProps = primaryButtonAction
            ? {
                context,
                primaryAction: primaryButtonAction.bind(null, [mediaItem]),
                ...(mediaItem.type === MediaType.PHOTO || (mediaItem.type === MediaType.VIDEO && mediaItem.url)
                  ? { lightBoxAction }
                  : {})
              }
            : undefined;
          break;
        case MediaGridContext.PAGE:
        case MediaGridContext.FIND_IMAGES:
          actionProps =
            primaryButtonAction && secondaryButtonAction
              ? {
                  context,
                  primaryAction: primaryButtonAction.bind(null, [mediaItem]),
                  secondaryAction: secondaryButtonAction.bind(null, [mediaItem]),
                  ...(mediaItem.type === MediaType.PHOTO || (mediaItem.type === MediaType.VIDEO && mediaItem.url)
                    ? { lightBoxAction }
                    : {})
                }
              : undefined;
          break;
        case MediaGridContext.POST_EDITOR_MEDIA_LIBRARY:
        case MediaGridContext.POST_EDITOR_FIND_IMAGES:
        default:
          actionProps = undefined;
      }

      return actionProps ? ActionMenuList(actionProps) : [];
    },
    [context, primaryButtonAction, secondaryButtonAction, setSelectedMediaItemForLightbox, setShowLightboxModal]
  );

  const mediaItemList = useMemo(
    () =>
      mediaItems.map((mediaItem: MediaItem) => (
        <MediaGridItem
          context={context}
          shareHandler={primaryButtonAction}
          key={`media-item-${mediaItem.masonryId}-${mediaItem.type}-${context}`}
          mediaItem={mediaItem}
          setShowLightboxModal={setShowLightboxModal}
          setSelectedMediaItemForLightbox={setSelectedMediaItemForLightbox}
          selectedMediaItems={selectedMediaItems}
          checkboxHandler={checkboxHandler}
          menuListItems={actionMenuListItems(mediaItem)}
          setImagesIdsWithErrorSelected={setImagesIdsWithErrorSelected}
        />
      )),
    [
      context,
      mediaItems,
      setShowLightboxModal,
      setSelectedMediaItemForLightbox,
      selectedMediaItems,
      checkboxHandler,
      primaryButtonAction,
      actionMenuListItems,
      setImagesIdsWithErrorSelected
    ]
  );

  // Compute the number of columns, not to exceed the number of items to be rendered
  // If the number of items is less than number of columns, limit the max width
  // so that we render columns without excessive spacing or large images
  const columnCount = props.parentWidth ? Math.floor(props.parentWidth / 252) : 4;
  const columnBreakPoints = columnCount ? columnCount : 1;
  const mediaItemCount = props.mediaItems.length;

  const masonryClasses: MasonryClasses = {
    root: classes.MUIRoot
  };

  const primaryAction =
    props.primaryButtonAction && props.primaryButtonAction.bind(null, getSelectedMediaItems(props.selectedMediaItems));
  const secondaryAction =
    props.secondaryButtonAction &&
    props.secondaryButtonAction.bind(null, getSelectedMediaItems(props.selectedMediaItems));

  const lbMedia: MediaItem[] = props.selectedMediaItemForLightbox ? [props.selectedMediaItemForLightbox] : [];
  const secondaryActionBox =
    props.secondaryButtonAction && props.selectedMediaItemForLightbox?.id
      ? () => {
          props.setShowLightboxModal(false);
          props.secondaryButtonAction(lbMedia);
        }
      : () => props.setShowLightboxModal(false);
  const gridWrapperClasses = classNames({ [classes.GridWrapper]: props.context === MediaGridContext.PAGE && false });

  return (
    <>
      <div ref={masonryRef} style={{ maxWidth: (mediaItemCount + 1) * 320 }} className={gridWrapperClasses}>
        <Masonry
          columns={columnBreakPoints < mediaItemCount ? columnBreakPoints : mediaItemCount}
          spacing={2}
          classes={masonryClasses}>
          {mediaItemList}
        </Masonry>
      </div>

      {Object.values(props.selectedMediaItems).length > 0 && (
        <FloatingActionBox context={props.context} itemsSelectedCount={Object.values(props.selectedMediaItems).length}>
          <ActionButtonGroup
            context={props.context}
            primaryButtonAction={primaryAction}
            secondaryButtonAction={secondaryAction}
            idsInvalidImagesSelected={imagesIdsWithErrorSelected}
          />
        </FloatingActionBox>
      )}

      {props.showLightboxModal && props.selectedMediaItemForLightbox && (
        <LightboxModal
          onClose={() => {
            props.setShowLightboxModal(false);
            props.setSelectedMediaItemForLightbox(undefined);
          }}
          mediaItem={props.selectedMediaItemForLightbox}
          isUnsplash={props.isUnsplash}
          context={context}
          primaryButtonAction={() => {
            props.primaryButtonAction &&
              props.selectedMediaItemForLightbox &&
              props.primaryButtonAction([props.selectedMediaItemForLightbox]);
            props.setShowLightboxModal(false);
          }}
          secondaryButtonAction={() => secondaryActionBox()}
        />
      )}
    </>
  );
};
